<template>
    <canvas></canvas>
</template>

<script>
import * as lib from 'Root/lib/lib.js'
import { CategoryScale, Chart, Legend, LineController, LineElement, LinearScale, PointElement, Tooltip } from 'chart.js'

Chart.register(LineElement, PointElement, LineController, CategoryScale, LinearScale, Tooltip, Legend)

export default {
    props: {
        labels: {
            type: Array,
            default: () => {
                return []
            },
        },
        datasets: {
            type: Array,
            default: () => {
                return []
            },
        },
        locale: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            colors: ['#f2a21a', '#31803e'],

            updateDebounce: 0,
        }
    },
    watch: {
        labels() {
            if (typeof this.chart === 'undefined') {
                return
            }

            this.updateChartData()
        },
        datasets() {
            if (typeof this.chart === 'undefined') {
                return
            }

            this.updateChartData()
        },
    },

    mounted() {
        this.elem = $(this.$el)

        setTimeout(() => {
            this._iniChart()
        })
    },
    methods: {
        _iniChart() {
            if (!lib.hasValue(this.labels) || !lib.hasValue(this.datasets)) {
                return
            }

            const ctx = this.elem[0].getContext('2d')

            const datasets = this.datasets.map((dataset, i) => {
                return {
                    ...dataset,
                    tension: 0,
                    backgroundColor: 'rgba(0,0,0,0)',
                    borderWidth: 2,
                    pointBorderWidth: 0,
                    pointHoverBorderWidth: 0,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    borderColor: this.colors[i],
                    pointBackgroundColor: this.colors[i],
                    pointHoverBackgroundColor: this.colors[i],
                    pointBorderColor: this.colors[i],
                }
            })

            const doubleYAxis = this.datasets.findIndex((i) => i.yAxisID) >= 0

            const scales = {
                x: {
                    title: {
                        padding: {
                            top: 12,
                        },
                        font: {
                            family: 'Gotham Greek',
                            weight: 'bold',
                        },
                    },
                    ticks: {
                        font: {
                            family: 'Gotham Greek',
                            weight: 'bold',
                        },
                        color: '#00327d',
                        autoSkip: false,
                    },
                    grid: {
                        drawOnChartArea: false,
                        drawBorder: false,
                        tickColor: 'rgba(0,0,0,0)',
                    },
                },
                y: {
                    min: lib.hasValue(this.datasets[0].yAxisMin) ? this.datasets[0].yAxisMin : null,
                    max: lib.hasValue(this.datasets[0].yAxisMax) ? this.datasets[0].yAxisMax : null,
                    title: {
                        display: doubleYAxis,
                        text: datasets[0].label,
                        color: this.colors[0],
                        font: {
                            family: 'Gotham Greek',
                            weight: 'bold',
                        },
                    },
                    ticks: {
                        font: {
                            family: 'Gotham Greek',
                            weight: 'bold',
                        },
                        color: '#00327d',
                        callback: (value) => {
                            return parseFloat(value.toFixed(2)).toLocaleString(this.locale)
                        },
                    },
                    grid: {
                        drawOnChartArea: false,
                        drawBorder: false,
                        tickColor: 'rgba(0,0,0,0)',
                    },
                },
            }

            if (doubleYAxis && datasets.length === 2) {
                scales[datasets[1].yAxisID] = {
                    position: 'right',
                    min: lib.hasValue(this.datasets[1].yAxisMin) ? this.datasets[1].yAxisMin : null,
                    max: lib.hasValue(this.datasets[1].yAxisMax) ? this.datasets[1].yAxisMax : null,
                    title: {
                        display: true,
                        text: datasets[1].label,
                        color: this.colors[1],
                        font: {
                            family: 'Gotham Greek',
                            weight: 'bold',
                        },
                    },
                    ticks: {
                        font: {
                            family: 'Gotham Greek',
                            weight: 'bold',
                        },
                        color: '#00327d',
                        callback: (value) => {
                            return parseFloat(value.toFixed(2)).toLocaleString(this.locale)
                        },
                    },
                    grid: {
                        drawOnChartArea: false,
                        drawBorder: false,
                        tickColor: 'rgba(0,0,0,0)',
                    },
                }
            }

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: this.labels,
                    datasets: datasets,
                },
                options: {
                    locale: this.locale,
                    aspectRatio: 2,
                    maintainAspectRatio: false,
                    scales: scales,
                    plugins: {
                        legend: {
                            display: !doubleYAxis,
                            position: 'bottom',
                            labels: {
                                boxWidth: 0,
                                font: {
                                    family: 'Gotham Greek',
                                    weight: 'bold',
                                },
                                generateLabels: (chart) => {
                                    const datasets = chart.data.datasets
                                    const legends = []

                                    datasets.forEach((data) =>
                                        legends.push({
                                            text: data.label,
                                            fontColor: data.borderColor,
                                            fillStyle: data.borderColor,
                                        })
                                    )
                                    return legends
                                },
                                onClick() {
                                    return
                                },
                            },
                            onClick() {
                                return
                            },
                        },
                        datalabels: {
                            display: true,
                        },
                        tooltip: {
                            intersect: false,
                            backgroundColor: '#00327d',
                            padding: 10,
                            cornerRadius: 0,
                            titleMarginBottom: 10,
                            yAlign: 'bottom',
                            boxWidth: 200,
                            titleFont: {
                                family: 'Gotham Greek',
                                size: 12,
                                weight: 400,
                            },
                            bodyFont: {
                                family: 'Gotham Greek',
                                size: 14,
                                weight: 700,
                            },
                            displayColors: false,
                            callbacks: {
                                label: (context) => {
                                    return context.formattedValue
                                },
                                beforeLabel: (context) => {
                                    return `${this.datasets[context.datasetIndex].label}:`
                                },
                            },
                        },
                    },
                },
            })
        },

        updateChartData() {
            clearTimeout(this.updateDebounce)

            this.updateDebounce = setTimeout(() => {
                this.chart.data.datasets.forEach((dataset, index) => {
                    dataset.data = this.datasets[index]
                    dataset.borderColor = this.colors[index]
                    dataset.pointHoverBackgroundColor = this.colors[index]
                })
                this.chart.data.labels = this.labels
                const labels = this.chart.data.labels

                this.chart.options.scales.x.ticks.callback = (value, index, values) => {
                    return index % parseInt(values.length / 2) === 0 || index === values.length - 1 ? labels[index] : ''
                }

                this.chart.update()
            }, 100)
        },
    },
}
</script>
